// Exact-decimal measure helpers for the office inventory screens (spec 06
// §2.10 / D-042). Every quantity on the module-06 wire is a DECIMAL STRING
// mirroring `numeric(18,6)` — never a JSON number — so the UI must not route
// one through IEEE-754 before showing it or comparing it.
//
// These helpers parse the wire text into a scaled BigInt (×1e6, the contract's
// 6-dp scale) and do all comparison / ratio work there. `Number` appears only
// at the very last step, where a value is turned into DISPLAY text (a bar
// width, a percentage with three decimals) — never where a quantity is
// compared, summed or persisted.
//
// Pure, so the drift/donut rules of §4.2 are unit-testable without rendering.

import { unitSymbol } from "../../../../lib/quantity";
import { shortId as shortIdTail } from "../../../../lib/short-id";
import { normalizeTypedDecimal } from "../../sales/lib/money";

/** The contract's fixed scale: `numeric(18,6)`. */
const SCALE = 1_000_000n;

/**
 * Parse an exact decimal measure string into its scaled BigInt (×1e6).
 * Returns `undefined` for null/undefined/garbage rather than throwing inside a
 * render — a malformed measure must degrade to "unknown", never to 0.
 */
/**
 * What a person typed into a measure box, in the wire's exact decimal form
 * (#286, D-328). A ru or kk keyboard types `1543,5`; the reader's own
 * separators are normalised the way the sales price box normalises them
 * (#204). Text that is not confidently a number comes back trimmed and
 * otherwise unchanged, so the caller's own pattern still refuses it.
 */
export function typedMeasure(text: string, locale: string): string {
  return normalizeTypedDecimal(text.trim(), locale);
}

export function parseMeasure(text: string | null | undefined): bigint | undefined {
  if (text === null || text === undefined) return undefined;
  const match = /^(-?)(\d{1,18})(?:\.(\d{1,6}))?$/u.exec(text.trim());
  if (match === null) return undefined;
  const [, sign, whole, frac] = match;
  if (whole === undefined) return undefined;
  const fracPadded = (frac ?? "").padEnd(6, "0");
  const magnitude = BigInt(whole) * SCALE + BigInt(fracPadded);
  return sign === "-" ? -magnitude : magnitude;
}

/**
 * Render a measure string for humans: group separators from the active locale,
 * trailing zeros trimmed to `maxDp` (default 3 — a warehouse reads `1 543 m`,
 * not `1543.000000`). Unparseable input is echoed verbatim rather than blanked:
 * showing the raw server text is honest, showing nothing is not.
 */
export function formatMeasure(
  text: string | null | undefined,
  options: { locale?: string | undefined; maxDp?: number | undefined } = {},
): string {
  if (text === null || text === undefined) return "";
  const scaled = parseMeasure(text);
  if (scaled === undefined) return text;
  const maxDp = options.maxDp ?? 3;
  const negative = scaled < 0n;
  const abs = negative ? -scaled : scaled;
  const whole = abs / SCALE;
  const frac = abs % SCALE;
  // Truncate-free rounding of the fraction to maxDp, half-up on the display
  // digit only (this is presentation, never a ledger value).
  const divisor = 10n ** BigInt(6 - maxDp);
  const rounded = (frac + divisor / 2n) / divisor;
  const carried = rounded === 10n ** BigInt(maxDp) ? 1n : 0n;
  const fracDigits = carried === 1n ? 0n : rounded;
  const wholeOut = whole + carried;
  let fracText = maxDp > 0 ? fracDigits.toString().padStart(maxDp, "0").replace(/0+$/u, "") : "";
  if (fracText === "0") fracText = "";
  const nf = new Intl.NumberFormat(options.locale, { useGrouping: true });
  const wholeText = nf.format(wholeOut);
  const sign = negative && (wholeOut !== 0n || fracText.length > 0) ? "-" : "";
  // The reader's decimal mark: "1 543,5" in French and Russian. A hardcoded
  // point here printed "1 543.5" beside formatQuantity's "1 543,5".
  const mark = nf.formatToParts(1.5).find((part) => part.type === "decimal")?.value ?? ".";
  return fracText.length > 0 ? `${sign}${wholeText}${mark}${fracText}` : `${sign}${wholeText}`;
}

/**
 * An issue's unreturned remainder, exactly: `issued` minus the magnitude of
 * each return, on the ×1e6 scaled integers and written back in the wire's form
 * (no trailing zeros). `undefined` when nothing is left or a side is unreadable.
 */
export function exactRemainder(issued: string, returns: readonly string[]): string | undefined {
  const start = parseMeasure(issued);
  if (start === undefined || start <= 0n) return undefined;
  let left = start;
  for (const text of returns) {
    const back = parseMeasure(text);
    if (back === undefined) return undefined;
    left -= back < 0n ? -back : back;
  }
  if (left <= 0n) return undefined;
  const whole = left / SCALE;
  const frac = (left % SCALE).toString().padStart(6, "0").replace(/0+$/u, "");
  return frac.length > 0 ? `${whole.toString()}.${frac}` : whole.toString();
}

/** A measure plus its unit, the way every §4 screen labels a quantity. The
 * unit prints as its symbol in the reader's language ("mm²", "шт."), never as
 * the wire code. */
export function formatMeasureWithUom(
  text: string | null | undefined,
  uom: string | null | undefined,
  options: { locale?: string | undefined; maxDp?: number | undefined } = {},
): string {
  const value = formatMeasure(text, options);
  if (value.length === 0) return "";
  if (uom === null || uom === undefined || uom.length === 0) return value;
  const count = Number(text);
  return `${value} ${unitSymbol(uom, {
    locale: options.locale,
    ...(Number.isFinite(count) ? { count } : {}),
  })}`;
}

/**
 * `remaining / capacity` as a 0–1 fraction for the §4.2 donut and the §4.1
 * remaining/capacity bar. `undefined` when capacity is absent or not positive
 * (a bare piece with no capacity has no fill ratio — the UI shows the numeric
 * only, never a fabricated full ring).
 */
export function fillRatio(
  remaining: string | null | undefined,
  capacity: string | null | undefined,
): number | undefined {
  const r = parseMeasure(remaining);
  const c = parseMeasure(capacity);
  if (r === undefined || c === undefined || c <= 0n) return undefined;
  if (r <= 0n) return 0;
  if (r >= c) return 1;
  // One division, at display precision (4 dp of a ratio = 0.01% of a ring).
  return Number((r * 10_000n) / c) / 10_000;
}

/**
 * §4.2 claimed-vs-measured drift: the signed percentage
 * `(initial − claimed) / claimed × 100`, computed on the scaled integers and
 * only then converted for display (3 dp). `undefined` when either value is
 * missing or `claimed` is zero — no baseline means no drift, never 0%.
 */
export function driftPct(
  claimed: string | null | undefined,
  initial: string | null | undefined,
): number | undefined {
  const c = parseMeasure(claimed);
  const i = parseMeasure(initial);
  if (c === undefined || i === undefined || c === 0n) return undefined;
  const delta = i - c;
  const abs = c < 0n ? -c : c;
  return Number((delta * 100_000n) / abs) / 1000;
}

/** §4.2: the drift badge fires past ±2% (the spec's `|Δ| > 2%` threshold). */
export const DRIFT_BADGE_THRESHOLD_PCT = 2;

export function driftExceedsThreshold(pct: number | undefined): boolean {
  return pct !== undefined && Math.abs(pct) > DRIFT_BADGE_THRESHOLD_PCT;
}

/** Signed percentage for display: `+8.9%` / `-2.4%`. */
export function formatPct(pct: number, dp = 1): string {
  const sign = pct > 0 ? "+" : "";
  return `${sign}${pct.toFixed(dp)}%`;
}

/** The `YYYY-MM-DD` parts of a business date, or `undefined` if it is not one. */
function businessDateParts(
  dateText: string | null | undefined,
): { y: number; m: number; d: number } | undefined {
  if (dateText === null || dateText === undefined) return undefined;
  const match = /^(\d{4})-(\d{2})-(\d{2})$/u.exec(dateText.trim());
  if (match === null) return undefined;
  const [, y, m, d] = match;
  if (y === undefined || m === undefined || d === undefined) return undefined;
  return { y: Number(y), m: Number(m), d: Number(d) };
}

/**
 * Render a business DATE (`date` on the wire — `eta_on`, `received_on`,
 * `expires_on`) for humans.
 *
 * `new Date("2026-08-05").toLocaleDateString()` is the bug this replaces: the
 * string parses as UTC MIDNIGHT and is then formatted in the viewer's zone, so
 * anyone at a negative offset reads the day before. A business date has no
 * instant in it at all, so it is formatted from its own parts with the
 * formatter pinned to UTC (§9.8).
 *
 * THE IMPLEMENTATION MOVED (surface review F9, 2026-08-12): it now lives in
 * `src/lib/datetime.ts`, the app's one date home, and this is a RE-EXPORT so
 * the module-06 call sites and their tests are untouched. It was a
 * byte-identical copy of that function — two implementations of one rule, and
 * the rule is exactly the kind that drifts silently when only one copy is
 * fixed.
 */
export { formatBusinessDate } from "../../../../lib/datetime";

/**
 * A wire TIMESTAMP for humans (`moved_at`, `counted_at`, `requested_at`) — the
 * same one formatter, re-exported here so a module-06 screen reaches for its
 * own lib and gets the app's answer.
 *
 * Every one of those columns used to call `new Date(x).toLocaleString(locale)`
 * directly, which is a THIRD shape beside `formatBusinessDate`'s medium date
 * and the browser's `<input type="date">`: three date formats in one area
 * (F9). `dateStyle: medium` + `timeStyle: short` is the app's, and it is what
 * module 04 has rendered since 04-A.
 */
export { formatTimestamp } from "../../../../lib/datetime";

/**
 * Whole days between a business date (`YYYY-MM-DD`, the facility's local date)
 * and today, positive when the date is in the PAST. Drives the §4.4
 * age-in-transit amber rule ("amber past ETA"). Dates only — never a timestamp
 * subtraction that a timezone could flip (§9.8).
 *
 * "Today" is the viewer's LOCAL calendar day, not the UTC one: in Almaty
 * (UTC+5/+6) the UTC date lags the local date for the first five to six hours
 * of every day, which is exactly the morning shift that acts on a late
 * transfer. (The facility's own zone is not on the wire — §5 serves no
 * facility timezone — so the viewer's calendar is the closest honest proxy;
 * recorded in the §4.4 row.)
 */
export function daysPastDate(dateText: string | null | undefined, now: Date = new Date()): number {
  const parts = businessDateParts(dateText);
  if (parts === undefined) return 0;
  const target = Date.UTC(parts.y, parts.m - 1, parts.d);
  const today = Date.UTC(now.getFullYear(), now.getMonth(), now.getDate());
  return Math.round((today - target) / 86_400_000);
}

/**
 * The instant a local calendar day STARTS / ENDS, as an ISO string for a
 * timestamp filter (§4.8's `moved_from` / `moved_to`).
 *
 * The ledger renders `moved_at` in the viewer's local time, so a filter built
 * from UTC midnights silently drops rows the same table labels as inside the
 * range. Building the bound from local calendar parts keeps the filter and the
 * column in the same clock.
 */
export function localDayStartIso(dateText: string): string | undefined {
  const parts = businessDateParts(dateText);
  if (parts === undefined) return undefined;
  return new Date(parts.y, parts.m - 1, parts.d, 0, 0, 0, 0).toISOString();
}

export function localDayEndIso(dateText: string): string | undefined {
  const parts = businessDateParts(dateText);
  if (parts === undefined) return undefined;
  return new Date(parts.y, parts.m - 1, parts.d, 23, 59, 59, 999).toISOString();
}

/** §4.4: a dispatched transfer is amber once today is past its ETA. */
export function isOverdue(etaOn: string | null | undefined, now: Date = new Date()): boolean {
  if (etaOn === null || etaOn === undefined) return false;
  return daysPastDate(etaOn, now) > 0;
}

/** Short uuid for tables where the full id is noise but identity still matters. */
export function shortId(id: string | null | undefined): string {
  if (id === null || id === undefined) return "";
  return shortIdTail(id);
}

/**
 * Relative "last seen" text for the §4.1 result cards ("2h ago"). Returns
 * `undefined` when there is no timestamp — the card then says "never scanned"
 * from its own dictionary rather than inventing a time.
 */
export function hoursSince(
  iso: string | null | undefined,
  now: Date = new Date(),
): number | undefined {
  if (iso === null || iso === undefined) return undefined;
  const then = Date.parse(iso);
  if (Number.isNaN(then)) return undefined;
  return (now.getTime() - then) / 3_600_000;
}
